import React from "react";

interface ServiceStatus {
  name: string;
  status: "operational" | "degraded" | "outage" | "maintenance";
  description: string;
  uptime: string;
}

const services: ServiceStatus[] = [
  {
    name: "Web Application",
    status: "operational",
    description: "Dashboard and canvas editor",
    uptime: "99.9%"
  },
  {
    name: "API Services",
    status: "operational",
    description: "REST API and authentication",
    uptime: "99.8%"
  },
  {
    name: "Workflow Engine (Firefly)",
    status: "operational",
    description: "Workflow execution and processing",
    uptime: "99.7%"
  },
  {
    name: "Real-time Collaboration",
    status: "operational",
    description: "WebSocket connections and sync",
    uptime: "99.6%"
  },
  {
    name: "File Storage",
    status: "operational",
    description: "Asset and workflow storage",
    uptime: "99.9%"
  }
];

const StatusBadge = ({ status }: { status: ServiceStatus["status"] }) => {
  const styles = {
    operational: "bg-green-100 text-green-800 border-green-200",
    degraded: "bg-yellow-100 text-yellow-800 border-yellow-200",
    outage: "bg-red-100 text-red-800 border-red-200",
    maintenance: "bg-blue-100 text-blue-800 border-blue-200"
  };

  const labels = {
    operational: "Operational",
    degraded: "Degraded Performance",
    outage: "Major Outage",
    maintenance: "Maintenance"
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
};

export default function Status() {
  const overallStatus = services.every(s => s.status === "operational") ? "operational" : "degraded";

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Lavender Flow Status</h1>
          <div className="flex items-center justify-center gap-3">
            <StatusBadge status={overallStatus} />
            <span className="text-gray-600">All systems operational</span>
          </div>
        </div>

        {/* Current Status */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900">Current Status</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {services.map((service) => (
              <div key={service.name} className="p-6 flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{service.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">{service.uptime}</div>
                    <div className="text-xs text-gray-500">30 days</div>
                  </div>
                  <StatusBadge status={service.status} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Incidents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900">Recent Incidents</h2>
          </div>
          <div className="p-6">
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recent incidents</h3>
              <p className="text-gray-500">All systems have been running smoothly for the past 30 days.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Status updates are posted in real-time. For support, contact us at support@lavenderflow.dev</p>
        </div>
      </div>
    </div>
  );
}